import { useMemo } from 'react'
import { Box, Card, CardContent, Typography } from '@mui/material'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js'
import { Line } from 'react-chartjs-2'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
)

const defaultColors = {
  light: '#dcff90',
  main: '#cdff65',
  dark: '#9dd328',
  contrastText: '#121212',
}

function fmtDateShort(ts) {
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return String(ts)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

export default function StatChart({
  workouts = [],
  dataKey = 'efficiency', 
  label,             
  unit = '',            
  yMax,                   
  color = defaultColors,  
  formatValue,           
}) {
  const { labels, values } = useMemo(() => {
    if (!workouts.length) return { labels: [], values: [] }

    const valid = workouts
      .filter(w => w[dataKey] != null && (w.created || w.updated))
      .map(w => ({
        date: w.created || w.updated,
        value: Number(w[dataKey]),
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date))

    return {
      labels: valid.map(w => fmtDateShort(w.date)),
      values: valid.map(w => w.value),
    }
  }, [workouts, dataKey])

  const displayLabel = label || dataKey

  const data = {
    labels,
    datasets: [
      {
        data: values,
        borderColor: color.dark,
        backgroundColor: 'rgba(205, 255, 101, 0.18)',
        pointBackgroundColor: color.main,
        pointBorderColor: color.dark,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderWidth: 2,
        tension: 0.3,
        fill: true,
      },
    ],
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: color.contrastText,
        titleColor: color.light,
        bodyColor: color.light,
        displayColors: false,
        callbacks: {
          title: (items) => items?.[0]?.label || '',
          label: (context) => {
            const val = context.parsed.y
            if (formatValue) return formatValue(val)
            return `${displayLabel}: ${val}${unit}`
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: '#fff',
          maxTicksLimit: 6,
        },
        grid: { display: false },
        border: { display: false },
      },
      y: {
        beginAtZero: true,
        max: yMax,
        ticks: {
          color: '#fff',
          callback: (val) => `${val}${unit}`,
        },
        grid: {
          color: 'rgba(18, 18, 18, 0.08)',
        },
        border: { display: false },
      },
    },
  }

  if (!values.length) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 3, mt: 1 }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Нет данных
          </Typography>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        borderColor: 'rgba(18, 18, 18, 0.12)',
        backgroundColor: '#00000034',
      }}
    >
      <CardContent sx={{ p: 1 }}>
        <Typography variant="body2" sx={{ mt: 1, mb: 1 }}>
          {label}
        </Typography>
        <Box sx={{ height: 160 }}>
          <Line data={data} options={options} />
        </Box>
      </CardContent>
    </Card>
  )
}